import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [price, setPrice] = useState(10);
  const [premiumPrice, setPremiumPrice] = useState(19.99);
  const [premiumDays, setPremiumDays] = useState(30);
  const [isPremium, setIsPremium] = useState(false);
  const [premiumUntil, setPremiumUntil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingFor, setPayingFor] = useState(null);
  const [showPay, setShowPay] = useState(null);
  const [showPremium, setShowPremium] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/matches");
      setMatches(data.matches);
      setPrice(data.unlock_price);
      setPremiumPrice(data.premium_price);
      setPremiumDays(data.premium_days);
      setIsPremium(data.is_premium);
      setPremiumUntil(data.premium_until);
    } catch (e) {
      toast.error("Couldn't load matches");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe redirect
  useEffect(() => {
    const sid = params.get("session_id");
    const unlocked = params.get("unlocked");
    const premium = params.get("premium");
    if (sid) {
      pollStripe(sid, unlocked, premium);
    } else if (params.get("cancelled")) {
      toast.error("Payment cancelled");
      params.delete("cancelled");
      setParams(params);
    }
    // eslint-disable-next-line
  }, []);

  const pollStripe = async (sessionId, unlocked, premium, attempts = 0) => {
    if (attempts >= 8) { toast.error("Payment timed out"); return; }
    try {
      const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
      if (data.payment_status === "paid") {
        toast.success(premium ? "Premium activated for 30 days!" : "Match unlocked!");
        params.delete("session_id");
        params.delete("unlocked");
        params.delete("premium");
        setParams(params);
        await load();
        return;
      }
      if (data.status === "expired") { toast.error("Session expired"); return; }
      setTimeout(() => pollStripe(sessionId, unlocked, premium, attempts + 1), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  const startStripe = async (m) => {
    setPayingFor(m.id);
    try {
      const { data } = await api.post("/payments/stripe/checkout", {
        target_user_id: m.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Stripe error");
      setPayingFor(null);
    }
  };

  const startStripePremium = async () => {
    setPayingFor("premium");
    try {
      const { data } = await api.post("/payments/stripe/premium-checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Stripe error");
      setPayingFor(null);
    }
  };

  const startPaypalPremium = async () => {
    setPayingFor("premium");
    try {
      const { data: order } = await api.post("/payments/paypal/premium-order");
      const approveUrl = `https://www.sandbox.paypal.com/checkoutnow?token=${order.order_id}`;
      const popup = window.open(approveUrl, "paypal", "width=500,height=700");
      const timer = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(timer);
          try {
            const { data: cap } = await api.post("/payments/paypal/capture", { order_id: order.order_id });
            if (cap.payment_status === "paid") {
              toast.success("Premium activated!");
              setShowPremium(false);
              await load();
            } else { toast.error("Payment not completed"); }
          } catch (e) { toast.error(e?.response?.data?.detail || "PayPal capture failed"); }
          finally { setPayingFor(null); }
        }
      }, 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PayPal error");
      setPayingFor(null);
    }
  };

  const startPaypal = async (m) => {
    setPayingFor(m.id);
    try {
      // Create order
      const { data: order } = await api.post("/payments/paypal/order", {
        target_user_id: m.id,
        origin_url: window.location.origin,
      });
      // For MVP, redirect to PayPal approval (sandbox approve URL embedded in order links)
      // We'll fetch the order and find approve link
      const approveUrl = `https://www.sandbox.paypal.com/checkoutnow?token=${order.order_id}`;
      // Open in same window; on return PayPal sends them back — but we instead use a popup + capture
      const popup = window.open(approveUrl, "paypal", "width=500,height=700");
      const timer = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(timer);
          // Try capture
          try {
            const { data: cap } = await api.post("/payments/paypal/capture", {
              order_id: order.order_id,
              target_user_id: m.id,
            });
            if (cap.payment_status === "paid") {
              toast.success("Match unlocked!");
              setShowPay(null);
              await load();
            } else {
              toast.error("Payment not completed");
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || "PayPal capture failed");
          } finally {
            setPayingFor(null);
          }
        }
      }, 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PayPal error");
      setPayingFor(null);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="font-mono text-xs uppercase tracking-widest text-[#555]">[ YOUR MATCHES ]</div>
        <h1 className="font-display text-5xl tracking-tighter mt-1 mb-6">
          Mutual<br /><span className="text-[#E53935]">interest.</span>
        </h1>

        {/* Premium banner */}
        {isPremium ? (
          <div className="brutal bg-[#2E7D32] text-white p-4 mb-8 flex items-center justify-between" data-testid="premium-active">
            <div>
              <div className="font-mono text-[10px] uppercase">★ PREMIUM ACTIVE</div>
              <div className="font-display text-xl">All matches unlocked.</div>
            </div>
            <div className="font-mono text-xs text-right opacity-80">
              until {premiumUntil ? new Date(premiumUntil).toLocaleDateString() : "—"}
            </div>
          </div>
        ) : (
          <div className="brutal bg-black text-white p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="premium-banner">
            <div>
              <div className="font-mono text-[10px] uppercase text-[#E53935]">★ FACEMATCH PREMIUM</div>
              <div className="font-display text-2xl mt-1">${premiumPrice} · {premiumDays} days unlimited unlocks</div>
              <div className="font-mono text-xs text-white/70 mt-1">Skip the $10 per-match fee. See every mutual match instantly.</div>
            </div>
            <button
              data-testid="open-premium"
              onClick={() => setShowPremium(true)}
              className="px-6 py-3 bg-[#E53935] hover:bg-[#C62828] font-mono text-sm whitespace-nowrap"
            >
              GO PREMIUM →
            </button>
          </div>
        )}

        {loading ? (
          <div className="brutal bg-white p-12 text-center font-mono">LOADING...</div>
        ) : matches.length === 0 ? (
          <div data-testid="no-matches" className="brutal bg-white p-12 text-center">
            <div className="font-display text-3xl">NO MATCHES YET.</div>
            <p className="text-sm text-[#555] mt-2">Rate more profiles to find mutual interest.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {matches.map(m => (
              <MatchCard
                key={m.match_id}
                m={m}
                onUnlock={() => setShowPay(m)}
              />
            ))}
          </div>
        )}
      </div>

      {showPay && (
        <PayModal
          match={showPay}
          price={price}
          paying={payingFor === showPay.id}
          onStripe={() => startStripe(showPay)}
          onPaypal={() => startPaypal(showPay)}
          onClose={() => setShowPay(null)}
        />
      )}

      {showPremium && (
        <PremiumModal
          price={premiumPrice}
          days={premiumDays}
          paying={payingFor === "premium"}
          onStripe={startStripePremium}
          onPaypal={startPaypalPremium}
          onClose={() => setShowPremium(false)}
        />
      )}
    </div>
  );
}

function PremiumModal({ price, days, paying, onStripe, onPaypal, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="brutal bg-white max-w-md w-full" data-testid="premium-modal">
        <div className="p-6 border-b border-black flex items-center justify-between bg-black text-white">
          <div>
            <div className="font-mono text-[10px] text-[#E53935]">★ FACEMATCH PREMIUM</div>
            <div className="font-display text-2xl tracking-tighter">{days}-DAY PASS</div>
          </div>
          <button onClick={onClose} data-testid="premium-close" className="font-mono text-sm hover:text-[#E53935]">✕</button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <div className="font-display text-6xl tracking-tighter">${price}</div>
            <div className="font-mono text-[10px] text-[#555]">ONE-TIME · {days} DAYS</div>
          </div>
          <ul className="space-y-2 font-mono text-xs">
            <li>✓ UNLIMITED MATCH UNLOCKS</li>
            <li>✓ NO $10 PER-MATCH FEE</li>
            <li>✓ PRIORITY VISIBILITY IF YOU SCORE ≥ 8</li>
            <li>✓ AUTO-EXPIRES — NO RECURRING CHARGE</li>
          </ul>
          <button
            data-testid="premium-stripe"
            onClick={onStripe}
            disabled={paying}
            className="w-full py-4 bg-[#E53935] text-white font-mono hover:bg-[#C62828] disabled:opacity-60"
          >
            {paying ? "REDIRECTING..." : `PAY $${price} WITH CARD (STRIPE)`}
          </button>
          <button
            data-testid="premium-paypal"
            onClick={onPaypal}
            disabled={paying}
            className="w-full py-4 border-2 border-black font-mono hover:bg-black hover:text-white disabled:opacity-60"
          >
            {paying ? "OPENING PAYPAL..." : "PAY WITH PAYPAL"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MatchCard({ m, onUnlock }) {
  const locked = !m.unlocked;
  return (
    <div className="brutal bg-white overflow-hidden relative" data-testid={`match-card-${m.id}`}>
      <div className="relative">
        <img
          src={m.photo_base64}
          alt={locked ? "locked" : m.name}
          className={`w-full aspect-[4/5] object-cover ${locked ? "locked-blur" : ""}`}
        />
        {locked && (
          <div className="absolute inset-0 flex flex-col items-center justify-center glass-dark">
            <div className="font-mono text-xs tracking-widest mb-2">[ LOCKED MATCH ]</div>
            <div className="font-display text-4xl mb-4">$10</div>
            <button
              data-testid={`unlock-btn-${m.id}`}
              onClick={onUnlock}
              className="px-6 py-3 bg-[#E53935] text-white font-mono text-sm hover:bg-[#C62828]"
            >
              UNLOCK MATCH →
            </button>
          </div>
        )}
      </div>
      <div className="p-4 border-t border-black flex items-center justify-between">
        <div>
          <div className="font-display text-xl tracking-tighter">
            {locked ? "█████████" : `${m.name.toUpperCase()}, ${m.age}`}
          </div>
          <div className="font-mono text-[10px] text-[#555]">
            {m.distance_km != null ? `${m.distance_km} KM` : (m.city || "—")}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[9px] text-[#555]">AI</div>
          <div className="font-mono font-bold text-lg">{m.ai_rating ?? "—"}</div>
        </div>
      </div>
      {!locked && m.email && (
        <div className="px-4 pb-3 font-mono text-[11px] text-[#2E7D32]" data-testid={`contact-${m.id}`}>
          ✓ {m.email}
        </div>
      )}
    </div>
  );
}

function PayModal({ match, price, paying, onStripe, onPaypal, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="brutal bg-white max-w-md w-full" data-testid="pay-modal">
        <div className="p-6 border-b border-black flex items-center justify-between">
          <div className="font-display text-2xl tracking-tighter">UNLOCK MATCH</div>
          <button onClick={onClose} data-testid="pay-close" className="font-mono text-sm hover:text-[#E53935]">✕</button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-4">
            <img src={match.photo_base64} alt="" className="w-20 h-20 object-cover locked-blur brutal" />
            <div>
              <div className="font-mono text-xs">ONE-TIME PAYMENT</div>
              <div className="font-display text-5xl">${price.toFixed(0)}</div>
              <div className="font-mono text-[10px] text-[#555]">UNLOCKS NAME + CONTACT + DISTANCE</div>
            </div>
          </div>
          <button
            data-testid="pay-stripe"
            onClick={onStripe}
            disabled={paying}
            className="w-full py-4 bg-black text-white font-mono hover:bg-[#E53935] disabled:opacity-60"
          >
            {paying ? "REDIRECTING..." : "PAY WITH CARD (STRIPE)"}
          </button>
          <button
            data-testid="pay-paypal"
            onClick={onPaypal}
            disabled={paying}
            className="w-full py-4 border-2 border-black font-mono hover:bg-black hover:text-white disabled:opacity-60"
          >
            {paying ? "OPENING PAYPAL..." : "PAY WITH PAYPAL"}
          </button>
          <p className="font-mono text-[10px] text-[#555] text-center">
            STRIPE TEST MODE • PAYPAL SANDBOX
          </p>
        </div>
      </div>
    </div>
  );
}
